/**
 * Client de l'API de réputation — L8.
 *
 * **Aucun chemin de ce module n'entre dans la liste blanche du service worker** (invariant 17).
 * Les trois lectures de réputation sont pourtant anonymes et publiques, ce qui pourrait faire croire
 * à des référentiels : elles n'en sont pas. Une note change à chaque avis publié, et le service
 * worker resservirait une moyenne périmée — le pire cas pour un chiffre qui juge quelqu'un.
 *
 * **Les notes arrivent en nombres, jamais en chaînes formatées** (règle 9 du §6 de la conception
 * L10). `4,3` en français et `٤٫٣` en arabe : c'est `Intl` qui décide, à l'affichage.
 *
 * **Le dépôt prend un identifiant d'INVITATION, jamais un sujet** : il n'existe pas de route
 * « déposer un avis sur X ». C'est ce qui rend l'anti-abus du §8.2-1 structurel plutôt que vérifié.
 */

import { appeler } from './api'

export type NatureSujet = 'Property' | 'Landlord' | 'Tenant' | 'Roommate' | 'Household' | 'Agency'
export type DeclencheurCycle = 'LeaseEnd' | 'MidTerm' | 'MemberLeft'
export type StatutInvitation = 'Pending' | 'Submitted' | 'Declined' | 'Expired'
export type StatutAvis =
  | 'Draft'
  | 'Submitted'
  | 'PendingCounterpart'
  | 'Published'
  | 'Hidden'
  | 'Removed'

// ---------------------------------------------------------------------------
// Dépôt
// ---------------------------------------------------------------------------

export interface InvitationAvis {
  invitationId: string
  cycleId: string
  trigger: DeclencheurCycle
  subjectType: NatureSujet
  subjectRefId: string
  subjectDisplayName: string | null
  isBilateral: boolean
  status: StatutInvitation
  opensAt: string
  blindRevealAt: string
  closesAt: string
  relationStartedOn: string
  relationEndedOn: string | null
  /** Ce que j'ai déposé sur cette invitation ; nul tant que rien ne l'est. */
  myReview: MonAvis | null
}

/** Mon avis, tel que déposé : la note, le texte, et s'il est publié ou attend l'autre partie. */
export interface MonAvis {
  reviewId: string
  overallRating: number
  comment: string | null
  status: StatutAvis
  submittedAt: string | null
  publishedAt: string | null
}

export interface CritereAvis {
  code: string
  displayOrder: number
  labelFr: string
  labelAr: string
  labelEn: string
}

export interface FormulaireAvis {
  invitationId: string
  subjectType: NatureSujet
  subjectRefId: string
  subjectDisplayName: string | null
  isBilateral: boolean
  blindRevealAt: string
  closesAt: string
  relationStartedOn: string
  relationEndedOn: string | null
  criteria: CritereAvis[]
}

export interface NoteCritere {
  criterionCode: string
  score: number
}

// ---------------------------------------------------------------------------
// Lecture
// ---------------------------------------------------------------------------

export interface MoyenneCritere {
  criterionCode: string
  average: number
}

export interface ReponseAvis {
  id: string
  authorUserId: string
  body: string
  language: string
  publishedAt: string
}

export interface AvisPublie {
  id: string
  authorUserId: string
  authorDisplayName: string | null
  subjectType: NatureSujet
  overallRating: number
  /** Déjà caviardé : la rédaction des coordonnées est une projection, jamais une écriture (D26). */
  comment: string | null
  language: string
  relationStartedOn: string
  relationEndedOn: string | null
  publishedAt: string
  scores: NoteCritere[]
  response: ReponseAvis | null
}

/**
 * La réputation d'un sujet, avec ses composantes.
 *
 * Les **trois** moyennes sont rendues, et c'est l'exigence d'explicabilité de D32 : quelqu'un qui
 * compte lui-même et trouve 4,6 là où l'écran affiche 4,3 doit comprendre l'écart, au lieu de
 * conclure que la plateforme triche.
 *
 * `isDisplayable` faux : on affiche « Nouveau » et **aucun chiffre** — les trois notes arrivent
 * nulles, pas seulement masquées à l'affichage (spec §8.2-6).
 */
export interface Reputation {
  subjectType: NatureSujet
  subjectId: string
  isDisplayable: boolean
  displayedRating: number | null
  plainAverageRating: number | null
  recencyWeightedRating: number | null
  reviewCount: number
  formulaVersion: string
  halfLifeMonths: number
  perCriterion: MoyenneCritere[]
  reviews: {
    items: AvisPublie[]
    page: number
    size: number
    total: number
  }
  /** Le référentiel des critères de cette nature de sujet, avec ses libellés — pour lire sans formulaire. */
  criteria: CritereAvis[]
}

// ---------------------------------------------------------------------------
// Appels
// ---------------------------------------------------------------------------

export function listerInvitations(): Promise<InvitationAvis[]> {
  return appeler<InvitationAvis[]>('/me/review-invitations')
}

export function lireFormulaire(invitationId: string): Promise<FormulaireAvis> {
  return appeler<FormulaireAvis>(`/review-invitations/${invitationId}`)
}

export function deposerAvis(
  invitationId: string,
  scores: NoteCritere[],
  comment: string | null,
  language: string,
): Promise<{ reviewId: string }> {
  return appeler(`/review-invitations/${invitationId}/review`, {
    method: 'POST',
    body: JSON.stringify({ scores, comment, language }),
  })
}

export function refuserInvitation(invitationId: string): Promise<void> {
  return appeler(`/review-invitations/${invitationId}/decline`, { method: 'POST' })
}

export function lireReputationUtilisateur(
  userId: string,
  page = 1,
  size = 10,
): Promise<Reputation> {
  return appeler<Reputation>(`/users/${userId}/reputation?page=${page}&size=${size}`)
}

export function lireReputationBien(propertyId: string, page = 1, size = 10): Promise<Reputation> {
  return appeler<Reputation>(`/properties/${propertyId}/reputation?page=${page}&size=${size}`)
}

export function lireReputationFoyer(householdId: string, page = 1, size = 10): Promise<Reputation> {
  return appeler<Reputation>(`/households/${householdId}/reputation?page=${page}&size=${size}`)
}

export function repondreAvis(
  reviewId: string,
  body: string,
  language: string,
): Promise<{ responseId: string }> {
  return appeler(`/reviews/${reviewId}/response`, { method: 'POST', body: JSON.stringify({ body, language }) })
}

export function signalerAvis(
  reviewId: string,
  reasonCode: string,
  description: string | null,
): Promise<{ reportId: string }> {
  return appeler(`/reviews/${reviewId}/report`, {
    method: 'POST',
    body: JSON.stringify({ reasonCode, description }),
  })
}

export function contesterAvis(
  reviewId: string,
  description: string,
): Promise<{ disputeId: string }> {
  return appeler(`/reviews/${reviewId}/contest`, { method: 'POST', body: JSON.stringify({ description }) })
}

export function repondreContestation(
  disputeId: string,
  body: string,
): Promise<{ messageId: string }> {
  return appeler(`/disputes/${disputeId}/answer`, { method: 'POST', body: JSON.stringify({ body }) })
}
